import { useEffect, useState } from 'react'
import { IconX } from '../icons'
import { fetchHistory, type HistoryItem } from '../lib/history'

/**
 * 我的历史：这个账号处理过的那几趟，点一下回到它。
 *
 * 2026-09-11 用户裁决：「登录之后，要能够查看我的历史，不然找不到之前的东西。」
 *
 * 为什么是一排图而不是一张表：人回来找的是**那张图**，不是作业号、时间戳或读数。
 *   缩略图并排摆着，认出来只需要一眼；表格要人读完每一行才知道哪行是自己要的那趟。
 *   所以每张卡上只有三样：图、什么时候、走的哪条路。读数留给点进去之后的完成屏。
 * 没跑出成品的那几趟（失败、取消、停在闸上）用原图当封面，并且照实说结局——
 *   它们同样要认得出来，尤其「停在闸上」那种：米粒已经预扣，回去点一下就接着跑。
 * 默认只摆一整行（6 张），其余折起来。这块在首屏案例底下，它不该把「修成什么样」那段挤下去。
 *
 * 两个入口，同一份列表：首屏这一块（`History`），以及作业跑起来之后标题条上那颗
 * 按钮拉开的抽屉（`HistoryPanel`）——2026-09-11 用户问「历史记录只能首页看？」。
 * 只摆在首屏的话，正在看某一趟时想翻回上一趟，只能先「换一张图」把手里这趟丢掉。
 *
 * 取数看 lib/history.ts（广州签只读通行证，首尔回列表）。这里不做「删除」：
 * 产物挂在账号上，删除走主站的账号注销那条路（见 README 的「产物不再限时清理」）。
 */
const FOLD_AT = 6

/** 结局里需要照实说出来的那几种。`done` 不在表里——封面就是成品，不必再说一遍。 */
const STATE_TEXT: Record<HistoryItem['state'], string> = {
  done: '',
  stalled: '未达到交付标准',
  failed: '处理失败',
  cancelled: '已取消',
  expired: '已超时',
  waiting: '待确认',
  running: '处理中',
}

/**
 * 取列表。`active` 为假时不取：抽屉没拉开之前，这一趟请求没人看。
 * 每次拉开都重取一遍（刚跑完那趟要排进去），旧的那份留在手里顶着，
 * 于是第二次拉开是直接摆出来，而不是先空一下再填。
 */
function useHistory(active: boolean) {
  const [items, setItems] = useState<HistoryItem[] | null>(null)
  useEffect(() => {
    if (!active) return
    const ac = new AbortController()
    fetchHistory(ac.signal).then(setItems).catch(() => {})
    return () => ac.abort()
  }, [active])
  return items
}

/** 一张卡。`now` 是「你正在看的就是这一趟」——点它没有意义，摆个记号免得白点。 */
function Card({ it, now, onOpen }: {
  it: HistoryItem
  now: boolean
  onOpen: (jobId: string) => void
}) {
  return (
    <li>
      <button className={`hist-card${now ? ' is-now' : ''}`} onClick={() => onOpen(it.id)}>
        <span className="hist-shot">
          {it.coverUrl
            ? <img src={it.coverUrl} alt="" loading="lazy" />
            : <i className="hist-gone">已清理</i>}
          {now && <i className="hist-now">当前查看</i>}
        </span>
        <span className="hist-meta">
          <b>{when(it.createdAt)}</b>
          <i>
            {STATE_TEXT[it.state] ||
              `${it.deep ? '深度修复' : '无损清理'} · ${it.pixels[0]}×${it.pixels[1]}`}
          </i>
        </span>
      </button>
    </li>
  )
}

export function History({ onOpen }: { onOpen: (jobId: string) => void }) {
  const items = useHistory(true)
  const [all, setAll] = useState(false)

  // 没登录、没跑过、或者接口不通：整块不出现。空状态卡片在这里没有用——
  // 它占一屏的位置，却只说「这里什么都没有」，而用户此刻要做的事就在它上面。
  if (!items || items.length === 0) return null
  const shown = all ? items : items.slice(0, FOLD_AT)

  return (
    <section className="hist">
      <div className="sec-head">
        <div className="kicker">History</div>
        <h2>我处理过的</h2>
        {/* 「都挂在你的账号上」曾经写在这里，可这份列表来自首尔，而首尔的清理任务
            隔天就把作业目录与那一行删了（cleanup.py 的 TTL）——第二天回来这块整个
            不出现，用户会以为东西丢了。产物本身是进了盘的，所以路要指到盘上去。 */}
        <p>
          {'最近这几趟点开就回到完成页。产物都存进了你的米羊盘，更早的在那儿找：'}
          <a href="https://miyang.cn/console/drive" target="_blank" rel="noreferrer">
            {'打开米羊盘 →'}
          </a>
        </p>
      </div>

      <ul className="hist-grid">
        {shown.map(it => <Card key={it.id} it={it} now={false} onOpen={onOpen} />)}
      </ul>

      {!all && items.length > FOLD_AT && (
        <button className="hist-more" onClick={() => setAll(true)}>
          {`还有 ${items.length - FOLD_AT} 趟`}
        </button>
      )}
    </section>
  )
}

/**
 * 同一份列表的抽屉版：作业跑起来之后，从标题条上那颗「我处理过的」拉开。
 *
 * 为什么是覆盖层而不是换一屏：手里这趟不必让位。抽屉一关，页面还停在刚才那一格；
 *   真点了别的一趟才走（onOpen 那条路里手里这趟只是松手，不取消，见 RipplePage）。
 * 这里给空状态，首屏那块不给：拉抽屉的人是专门来找东西的，什么都不说等于让他
 *   怀疑是不是没点开；首屏那块则是顺路经过，空着就该整个不出现。
 * 全部摆出来，不折叠：抽屉自己能滚，而「还有 N 趟」在一个已经拉开的面板里
 *   只是多一次点击。
 */
export function HistoryPanel({ open, currentId, onOpen, onClose }: {
  open: boolean
  /** 现在屏幕上是哪一趟。列表里那张卡标成「正在看」。 */
  currentId?: string | null
  onOpen: (jobId: string) => void
  onClose: () => void
}) {
  const items = useHistory(open)

  // Esc 关、开着的时候锁住页面滚动，与灯箱同一套（见 Lightbox 的注释）。
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('keydown', key)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="histp" role="dialog" aria-modal="true" aria-label="我处理过的"
         onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="histp-panel">
        <header className="histp-head">
          <div>
            <b>我处理过的</b>
            <span>点一下回到那一趟，手里这趟不会丢</span>
          </div>
          <button className="histp-x" onClick={onClose} title="关闭（Esc）" aria-label="关闭">
            <IconX size={16} />
          </button>
        </header>

        <div className="histp-body">
          {items === null ? (
            <p className="histp-note">正在取……</p>
          ) : items.length === 0 ? (
            <p className="histp-note">还没有别的。跑完一趟，它就挂在你的账号上，
              以后从这里回去。</p>
          ) : (
            <ul className="hist-grid is-panel">
              {items.map(it => (
                <Card key={it.id} it={it} now={it.id === currentId}
                      onOpen={id => (id === currentId ? onClose() : onOpen(id))} />
              ))}
            </ul>
          )}
          {/* 这份列表只有首尔还留着的那几趟（隔天就清）。产物在盘里是长期的，
              所以这层抽屉的底下也要有一条去盘里的路。 */}
          <p className="histp-note">
            {'更早的产物在你的米羊盘里：'}
            <a href="https://miyang.cn/console/drive" target="_blank" rel="noreferrer">
              {'打开米羊盘 →'}
            </a>
          </p>
        </div>
      </aside>
    </div>
  )
}

/**
 * 「什么时候」写成人话。
 *
 * 绝对时间戳（2026-09-11 22:14）要人先换算一次才知道是不是今天那趟；而找回自己的东西时，
 * 「今天 / 昨天」是最先想起来的那一维。跨过昨天之后再用日期——那时相对说法
 * （「3 天前」）反倒不好定位。
 */
function when(ms: number): string {
  const d = new Date(ms)
  const hm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)
  const days = Math.floor((midnight.getTime() - d.getTime()) / 86400000) + 1
  if (days <= 0) return Date.now() - ms < 60000 ? '刚刚' : `今天 ${hm}`
  if (days === 1) return `昨天 ${hm}`
  const md = `${d.getMonth() + 1} 月 ${d.getDate()} 日`
  return d.getFullYear() === new Date().getFullYear() ? `${md} ${hm}` : `${d.getFullYear()} 年 ${md}`
}
